import {
    type FC,
    type ReactNode,
    createContext,
    useContext,
    useMemo,
    useEffect,
    useState,
    useCallback,
    useRef,
} from 'react';
import { graphql, useRelayEnvironment, fetchQuery, useFragment } from 'react-relay';
import { getLocalRecentHistoryList } from 'dibs-recent-history/exports/getLocalRecentHistoryList.ts';
import { COLLECTIONS_COLORS } from './constants.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import {
    type PersonalizedCollectionsContextQuery,
    type PersonalizedCollectionsContextQuery$data,
} from './__generated__/PersonalizedCollectionsContextQuery.graphql.ts';
import { type PersonalizedCollectionsContextTrendingItemsQuery } from './__generated__/PersonalizedCollectionsContextTrendingItemsQuery.graphql.ts';
import { getAbTestV2 } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { type PersonalizedCollectionsContext_item$key } from './__generated__/PersonalizedCollectionsContext_item.graphql.ts';

const HOW_MANY_RECENTLY_VIEWED_ITEMS_TO_GET = 1;
const HOMEPAGE_RECENTLY_VIEWED_COUNT = 4;

const CATEGORY_CODE_TO_VERTICAL: Record<string, string> = {
    F: 'furniture',
    V: 'fashion',
    J: 'jewelry',
    A: 'art',
};

const TRENDING_ORDER = [
    CATEGORY_CODE_TO_VERTICAL.J,
    CATEGORY_CODE_TO_VERTICAL.F,
    CATEGORY_CODE_TO_VERTICAL.V,
    CATEGORY_CODE_TO_VERTICAL.A,
];

function getVerticalByCategoryCode(categoryCode: string | null | undefined): string {
    const baseCategoryCode = categoryCode?.split('_')[0] || 'F';
    return CATEGORY_CODE_TO_VERTICAL[baseCategoryCode] || 'furniture';
}

function getVerticalByItemId(itemId: string): string {
    const [vertical] = itemId.split('_');
    return CATEGORY_CODE_TO_VERTICAL[vertical.toUpperCase()] || 'furniture';
}

export type BannerConfig = {
    collectionId: string;
    inspiredByItemId: string | null;
    backgroundColor: string;
};

type PersonalizedCollectionsContextValue = {
    collectionIds: string[];
    inspiredByItemId: string | null;
    backgroundColorPair: string[];
    bannerConfigs: BannerConfig[];
};

const PersonalizedCollectionsContext = createContext<PersonalizedCollectionsContextValue | null>(
    null
);

const DEFAULT_CONTEXT_VALUE: PersonalizedCollectionsContextValue = {
    collectionIds: [],
    backgroundColorPair: [],
    inspiredByItemId: null,
    bannerConfigs: [],
};

const personalizedCollectionsContextItemFragment = graphql`
    fragment PersonalizedCollectionsContext_item on Item {
        serviceId
        categoryCode
    }
`;

const personalizedCollectionsContextQuery = graphql`
    query PersonalizedCollectionsContextQuery($recentlyViewedItemIds: [String!]!) {
        viewer {
            personalizedCollections(itemIds: $recentlyViewedItemIds) {
                collectionId
                itemId
                relatedCollections {
                    collectionId
                }
            }
        }
    }
`;

const personalizedCollectionsContextTrendingItemsQuery = graphql`
    query PersonalizedCollectionsContextTrendingItemsQuery {
        viewer {
            itemSearchTrendingItems(sellerVerticals: ["jewelry", "furniture", "fashion", "art"]) {
                itemIds
            }
        }
    }
`;

function subscribeToCollections(
    environment: ReturnType<typeof useRelayEnvironment>,
    itemIds: string[],
    onResult: (result: PersonalizedCollectionsContextQuery$data) => void,
    onError: () => void
): { unsubscribe: () => void } {
    return fetchQuery<PersonalizedCollectionsContextQuery>(
        environment,
        personalizedCollectionsContextQuery,
        { recentlyViewedItemIds: itemIds },
        { fetchPolicy: 'store-or-network' }
    ).subscribe({
        next: onResult,
        error: onError,
    });
}

function getBackgroundColor(verticals: string[], index: number, fallbackVertical?: string): string {
    const defaultColor = COLLECTIONS_COLORS.jewelry.primary;
    const vertical = verticals[index] || fallbackVertical || 'jewelry';
    const prevVertical = index > 0 ? verticals[index - 1] : undefined;
    const colorKey = prevVertical === vertical ? 'secondary' : 'primary';
    return COLLECTIONS_COLORS[vertical]?.[colorKey] ?? defaultColor;
}

type RecentlyViewedItem = { itemId: string; itemCategoryCode?: string | null };

function buildPdpBannerConfigs(
    result: PersonalizedCollectionsContextQuery$data | null,
    recentlyViewedItems: RecentlyViewedItem[],
    verticals: string[],
    fallbackCategoryCode: string | null | undefined
): BannerConfig[] {
    const personalizedCollections = (result?.viewer?.personalizedCollections || []).filter(
        filterFalsy
    );
    if (!personalizedCollections.length) return [];

    const fallbackVertical = getVerticalByCategoryCode(fallbackCategoryCode);

    if (personalizedCollections.length === 1) {
        const collection = personalizedCollections[0];
        const configs: BannerConfig[] = [];

        if (collection.collectionId) {
            configs.push({
                collectionId: collection.collectionId,
                inspiredByItemId: null,
                backgroundColor: getBackgroundColor(verticals, 0, fallbackVertical),
            });
        }

        const relatedId = collection.relatedCollections?.[0]?.collectionId;
        if (relatedId) {
            configs.push({
                collectionId: relatedId,
                inspiredByItemId: null,
                backgroundColor: getBackgroundColor(verticals, 1, fallbackVertical),
            });
        }

        return configs;
    }

    return recentlyViewedItems
        .map((rv, i) => {
            const collectionId = personalizedCollections.find(
                c => c.itemId === rv.itemId
            )?.collectionId;
            if (!collectionId) return null;
            return {
                collectionId,
                inspiredByItemId: i === 1 ? rv.itemId : null,
                backgroundColor: getBackgroundColor(verticals, i, fallbackVertical),
            };
        })
        .filter((c): c is BannerConfig => c !== null);
}

function buildHomepageBannerConfigs(
    result: PersonalizedCollectionsContextQuery$data | null,
    recentlyViewedItems: RecentlyViewedItem[],
    verticals: string[],
    usingTrendingItems: boolean
): BannerConfig[] {
    const personalizedCollections = (result?.viewer?.personalizedCollections || []).filter(
        filterFalsy
    );
    if (!personalizedCollections.length) return [];

    const count = Math.min(personalizedCollections.length, recentlyViewedItems.length);
    return personalizedCollections
        .slice(0, count)
        .map((pc, i) => {
            if (!pc?.collectionId) return null;
            return {
                collectionId: pc.collectionId,
                inspiredByItemId: usingTrendingItems ? null : (pc?.itemId ?? null),
                backgroundColor: getBackgroundColor(verticals, i),
            };
        })
        .filter((c): c is BannerConfig => c !== null);
}

function buildContextValueFromConfigs(
    bannerConfigs: BannerConfig[]
): PersonalizedCollectionsContextValue {
    if (!bannerConfigs.length) {
        return DEFAULT_CONTEXT_VALUE;
    }
    return {
        collectionIds: bannerConfigs.map(c => c.collectionId),
        backgroundColorPair: bannerConfigs.map(c => c.backgroundColor),
        inspiredByItemId: bannerConfigs.length > 1 ? bannerConfigs[1].inspiredByItemId : null,
        bannerConfigs,
    };
}

type PersonalizedCollectionsProviderProps = {
    item: PersonalizedCollectionsContext_item$key | null | undefined;
    children: ReactNode;
};

export const PersonalizedCollectionsProvider: FC<PersonalizedCollectionsProviderProps> = ({
    item: itemRef,
    children,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(personalizedCollectionsContextItemFragment, itemRef);
    const [result, setResult] = useState<PersonalizedCollectionsContextQuery$data | null>(null);
    const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

    const isClient = typeof window !== 'undefined';
    const shouldLoad =
        isClient && getAbTestV2('PDP_PersonalizedDynamicCatalogue_Dec2025')?.variant === 'variant';

    const recentlyViewedItems = useMemo(() => {
        if (!isClient) return [];

        const currentItem = {
            itemId: item?.serviceId || '',
            itemCategoryCode: item?.categoryCode || 'F',
        };

        const [recentlyViewedItem] =
            getLocalRecentHistoryList(
                [currentItem.itemId],
                HOW_MANY_RECENTLY_VIEWED_ITEMS_TO_GET
            ) || [];

        return recentlyViewedItem ? [currentItem, recentlyViewedItem] : [currentItem];
    }, [isClient, item]);

    const recentlyViewedItemsVerticals = useMemo(
        () =>
            recentlyViewedItems.map(({ itemCategoryCode }) =>
                getVerticalByCategoryCode(itemCategoryCode)
            ),
        [recentlyViewedItems]
    );

    const loadCollectionLookupQuery = useCallback(() => {
        if (!recentlyViewedItems.length) return;
        subscriptionRef.current?.unsubscribe();

        subscriptionRef.current = subscribeToCollections(
            environment,
            recentlyViewedItems.map(rv => rv.itemId),
            r => setResult(r),
            () => setResult(null)
        );
    }, [environment, recentlyViewedItems]);

    useEffect(() => {
        if (shouldLoad && recentlyViewedItems.length > 0) {
            loadCollectionLookupQuery();
        }

        return () => {
            subscriptionRef.current?.unsubscribe();
        };
    }, [loadCollectionLookupQuery, shouldLoad, recentlyViewedItems]);

    const contextValue = useMemo(
        () =>
            buildContextValueFromConfigs(
                buildPdpBannerConfigs(
                    result,
                    recentlyViewedItems,
                    recentlyViewedItemsVerticals,
                    item?.categoryCode
                )
            ),
        [result, recentlyViewedItems, recentlyViewedItemsVerticals, item?.categoryCode]
    );

    return (
        <PersonalizedCollectionsContext value={contextValue}>
            {children}
        </PersonalizedCollectionsContext>
    );
};

type HomepagePersonalizedCollectionsProviderProps = {
    children: ReactNode;
};

export const HomepagePersonalizedCollectionsProvider: FC<
    HomepagePersonalizedCollectionsProviderProps
> = ({ children }) => {
    const environment = useRelayEnvironment();
    const [collectionsResult, setCollectionsResult] =
        useState<PersonalizedCollectionsContextQuery$data | null>(null);
    const [trendingIds, setTrendingIds] = useState<string[]>([]);
    const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

    const isClient = typeof window !== 'undefined';
    const shouldLoad = isClient;

    const recentlyViewedItems = useMemo(() => {
        if (!isClient) return [];
        const list = getLocalRecentHistoryList([], HOMEPAGE_RECENTLY_VIEWED_COUNT) || [];
        return list
            .reverse()
            .map((entry: { itemId: string; itemCategoryCode?: string | null }) => ({
                itemId: entry.itemId,
                itemCategoryCode: entry.itemCategoryCode || 'F',
            }));
    }, [isClient]);

    const recentlyViewedItemsVerticals = useMemo(
        () =>
            recentlyViewedItems.map(({ itemCategoryCode }) =>
                getVerticalByCategoryCode(itemCategoryCode)
            ),
        [recentlyViewedItems]
    );

    /**
     * Loads collections for the homepage.
     * If the user has viewed at least 4 items, it loads the collections for the recently viewed items.
     * If the user has viewed less than 4 items, it loads the trending items and then the collections for the trending items.
     */
    const loadCollections = useCallback(() => {
        subscriptionRef.current?.unsubscribe();

        if (recentlyViewedItems.length > 3) {
            subscriptionRef.current = subscribeToCollections(
                environment,
                recentlyViewedItems.map(rv => rv.itemId),
                r => setCollectionsResult(r),
                () => setCollectionsResult(null)
            );
        } else {
            const subscription = fetchQuery<PersonalizedCollectionsContextTrendingItemsQuery>(
                environment,
                personalizedCollectionsContextTrendingItemsQuery,
                {},
                { fetchPolicy: 'store-or-network' }
            ).subscribe({
                next: trendingResult => {
                    const ids =
                        trendingResult?.viewer?.itemSearchTrendingItems?.itemIds
                            ?.filter(filterFalsy)
                            .sort(
                                (a, b) =>
                                    TRENDING_ORDER.indexOf(getVerticalByItemId(a)) -
                                    TRENDING_ORDER.indexOf(getVerticalByItemId(b))
                            ) || [];
                    setTrendingIds(ids);

                    if (ids.length > 0) {
                        subscriptionRef.current = subscribeToCollections(
                            environment,
                            ids,
                            r => setCollectionsResult(r),
                            () => setCollectionsResult(null)
                        );
                    }
                },
                error: () => setTrendingIds([]),
            });
            subscriptionRef.current = subscription;
        }
    }, [environment, recentlyViewedItems]);

    useEffect(() => {
        if (shouldLoad) {
            loadCollections();
        }

        return () => {
            subscriptionRef.current?.unsubscribe();
        };
    }, [shouldLoad, loadCollections]);

    const contextValue = useMemo((): PersonalizedCollectionsContextValue => {
        if (recentlyViewedItems.length > 3 && collectionsResult) {
            return buildContextValueFromConfigs(
                buildHomepageBannerConfigs(
                    collectionsResult,
                    recentlyViewedItems,
                    recentlyViewedItemsVerticals,
                    false
                )
            );
        }
        if (recentlyViewedItems.length < 4 && trendingIds.length > 0 && collectionsResult) {
            const trendingAsItems = trendingIds.map(id => ({
                itemId: id,
                itemCategoryCode: getVerticalByItemId(id) || 'F',
            }));
            return buildContextValueFromConfigs(
                buildHomepageBannerConfigs(
                    collectionsResult,
                    trendingAsItems,
                    trendingAsItems.map(t => t.itemCategoryCode),
                    true
                )
            );
        }
        return DEFAULT_CONTEXT_VALUE;
    }, [collectionsResult, recentlyViewedItems, recentlyViewedItemsVerticals, trendingIds]);

    return (
        <PersonalizedCollectionsContext value={contextValue}>
            {children}
        </PersonalizedCollectionsContext>
    );
};

export const usePersonalizedCollections = (): PersonalizedCollectionsContextValue => {
    const context = useContext(PersonalizedCollectionsContext);
    if (!context) {
        throw new Error(
            'usePersonalizedCollections must be used within PersonalizedCollectionsProvider or HomepagePersonalizedCollectionsProvider'
        );
    }
    return context;
};
